import type { StepConfig, Feature, PageCount, MonthlySupport, ContentReadiness, PriceBreakdown, EstimatorSelections } from './types';

// ─── Step Configuration ───────────────────────────────────────────────────────

export const STEPS: StepConfig[] = [
  {
    id: "org-type",
    title: "Tell us about your organization.",
    subtitle: "This helps us tailor our approach to your specific context and community.",
    field: "orgType",
    multiSelect: false,
    options: [
      { value: "nonprofit",   label: "Nonprofit",                     description: "501(c)(3) or similar mission-driven org", icon: "Sprout" },
      { value: "church",      label: "Church or Faith-Based Org",     description: "Houses of worship, ministries, faith communities", icon: "Church" },
      { value: "hoa",         label: "HOA or Community Association",  description: "Neighborhood or property owner associations", icon: "Home" },
      { value: "government",  label: "Local Government / Municipal",  description: "City, county, township, or public agency", icon: "Landmark" },
      { value: "other",       label: "Other",                         description: "Something else that we would love to hear about", icon: "Star" },
    ],
  },
  {
    id: "project-type",
    title: "What are you looking to do?",
    subtitle: "There are no wrong answers here, as even \"I'm not sure\" is a great place to start.",
    field: "projectType",
    multiSelect: false,
    options: [
      { value: "new",         label: "Brand new website",      description: "Starting from scratch with a fresh build", icon: "Sparkles" },
      { value: "redesign",    label: "Redesign existing site", description: "Updating or rebuilding what you already have", icon: "RefreshCw" },
      { value: "figuring-out", label: "Help me figure it out", description: "Not sure yet, so let's explore together", icon: "Lightbulb" },
    ],
  },
  {
    id: "page-count",
    title: "How many pages do you envision?",
    subtitle: "Think about each unique section: Home, About, Services, Contact, etc.",
    field: "pageCount",
    multiSelect: false,
    options: [
      { value: "1-5",  label: "1–5 pages",    description: "Simple, focused site that is great for getting started", icon: "FileText" },
      { value: "6-15", label: "6–15 pages",   description: "Standard site covering all your key areas", icon: "ClipboardList" },
      { value: "16-30", label: "16–30 pages", description: "Content-rich site with depth and detail", icon: "Library" },
      { value: "30+",  label: "30+ pages",    description: "Complex site with extensive content architecture", icon: "FolderRoot" },
    ],
  },
  {
    id: "features",
    title: "What features do you need?",
    subtitle: "Select everything that applies. Some are included by default and will be noted below.",
    field: "features",
    multiSelect: true,
    options: [
      { value: "event-calendar",   label: "Event calendar",               description: "Display and manage upcoming events", icon: "Calendar" },
      { value: "donation",         label: "Donation / payment",            description: "Accept donations or payments online", icon: "CreditCard" },
      { value: "member-portal",    label: "Member portal or login",        description: "Private area for members or staff", icon: "Lock" },
      { value: "document-library", label: "Document library",              description: "Upload and share files or resources", icon: "Folder" },
      { value: "multi-language",   label: "Multi-language support",        description: "Site content in multiple languages", icon: "Globe" },
      { value: "blog",             label: "Blog or news section",          description: "Publish articles, updates, or stories", icon: "PenLine" },
      { value: "contact-forms",    label: "Contact forms",                 description: "Included with every build at no extra cost", icon: "Mail" },
      { value: "accessibility",    label: "Accessibility (WCAG / ADA)",    description: "Full compliance audit and remediation", icon: "Accessibility" },
      { value: "seo",              label: "SEO setup",                     description: "Technical SEO and search visibility foundation", icon: "Search" },
      { value: "cms-training",     label: "CMS training",                  description: "We teach your team to manage the site as a standard service", icon: "GraduationCap" },
    ],
  },
  {
    id: "content",
    title: "How's your content coming along?",
    subtitle: "Content includes writing, photos, logos, and anything else that goes on the site.",
    field: "contentReadiness",
    multiSelect: false,
    options: [
      { value: "ready",        label: "I have everything ready",         description: "Copy, photos, and logos are all set and organized", icon: "CircleCheckBig" },
      { value: "some-help",    label: "I have some, but need help",      description: "We'll fill in what's missing together", icon: "Handshake" },
      { value: "from-scratch", label: "I need it created from scratch",  description: "Full content creation as part of the project", icon: "FilePen" },
    ],
  },
  {
    id: "support",
    title: "Do you want ongoing support?",
    subtitle: "We offer monthly care plans so your site stays healthy, updated, and growing.",
    field: "monthlySupport",
    multiSelect: false,
    options: [
      { value: "none",    label: "Just the build",                description: "One-time project, no ongoing commitment", icon: "Construction" },
      { value: "basic",   label: "Basic maintenance & updates",   description: "Security, backups, small content updates", icon: "Wrench" },
      { value: "managed", label: "Full managed care & strategy",  description: "Proactive strategy, design, and ongoing growth", icon: "Rocket" },
    ],
  },
];

// ─── Pricing Configuration ────────────────────────────────────────────────────

export const BASE_PRICES: Record<string, number> = {
  "1-5":  750,
  "6-15": 1500,
  "16-30": 3500,
  "30+":  5500,
};

export const FEATURE_PRICES: Record<Feature, number> = {
  "event-calendar":   300,
  "donation":         400,
  "member-portal":    800,
  "document-library": 250,
  "multi-language":   600,
  "blog":             200,
  "contact-forms":    0,   // Always included
  "accessibility":    500,
  "seo":              300,
  "cms-training":     0,   // Always included
};

export const CONTENT_PRICES: Record<string, number> = {
  "ready":        0,
  "some-help":    300,
  "from-scratch": 800,
};

export const MONTHLY_PRICES: Record<string, { min: number; max: number | null }> = {
  "none":    { min: 0,   max: null },
  "basic":   { min: 150, max: null },
  "managed": { min: 300, max: 500  },
};

// ─── Package Mapping ──────────────────────────────────────────────────────────

export const PACKAGES = [
  { name: "Foundation", threshold: 0,    maxThreshold: 2999  },
  { name: "Growth",     threshold: 3000, maxThreshold: 6499  },
  { name: "Signature",  threshold: 6500, maxThreshold: Infinity },
];

export function getPackageName(total: number): string {
  const pkg = PACKAGES.find(p => total >= p.threshold && total <= p.maxThreshold);
  return pkg?.name ?? "Signature";
}

// ─── Price Calculation ────────────────────────────────────────────────────────

export function calculatePrice(selections: EstimatorSelections): PriceBreakdown {
  const base = BASE_PRICES[selections.pageCount as PageCount] ?? 0;

  const addons = selections.features.reduce((sum, f) => {
    return sum + (FEATURE_PRICES[f as Feature] ?? 0);
  }, 0);

  const content = CONTENT_PRICES[selections.contentReadiness as ContentReadiness] ?? 0;
  const total = base + addons + content;
  const totalMax = Math.round(total * 1.2); // +20% range ceiling

  const monthlyConfig = MONTHLY_PRICES[selections.monthlySupport as MonthlySupport];
  const monthly     = monthlyConfig ? monthlyConfig.min : null;
  const monthlyMax  = monthlyConfig ? monthlyConfig.max : null;

  const packageName = getPackageName(total);

  return { base, addons, content, total, totalMax, monthly, monthlyMax, packageName };
}

// ─── Summary Sentence Builder ─────────────────────────────────────────────────

const ORG_LABELS: Record<string, string> = {
  nonprofit:   "your nonprofit",
  church:      "your faith-based organization",
  hoa:         "your community association",
  government:  "your municipality",
  other:       "your organization",
};

const PROJECT_LABELS: Record<string, string> = {
  "new":          "a brand new website",
  "redesign":     "a redesigned website",
  "figuring-out": "a thoughtfully planned website",
};

const PAGE_LABELS: Record<string, string> = {
  "1-5":  "1–5 focused pages",
  "6-15": "6–15 pages",
  "16-30": "16–30 pages",
  "30+":  "30+ pages",
};

export function buildSummary(selections: EstimatorSelections, packageName: string): string {
  const org     = ORG_LABELS[selections.orgType]     ?? "your organization";
  const project = PROJECT_LABELS[selections.projectType] ?? "a website";
  const pages   = PAGE_LABELS[selections.pageCount]  ?? "several pages";

  const hasAccessibility = selections.features.includes("accessibility");
  const accessibilityNote = hasAccessibility ? ", ADA-compliant" : "";

  return `Based on your needs, ${project} for ${org} with ${pages}${accessibilityNote} will be a great fit for our ${packageName} package.`;
}
